import { mapKeys, set } from 'lodash'
import { AppSpecData, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import {
	Component,
	Data,
	DataMap,
	InternalRefDataItem,
	RefSchemas,
	Structure,
	WidgetRefDataItem,
} from '@wix/thunderbolt-becky-types'
import { BuilderLegacyComponentsMap } from '@wix/thunderbolt-commons'

export const getBuilderComponents = (clientSpecMap: ViewerClientSpecMap): Set<string> => {
	const builderComponents = new Set<string>()
	Object.values(clientSpecMap).forEach((app: AppSpecData) => {
		app.components?.forEach((comp) => {
			if (comp.type === 'EDITOR_REACT_COMPONENT') {
				builderComponents.add(comp.data.type)
			}
		})
	})
	return builderComponents
}

export const fixBuilderComponent = (component: Component) => {
	set(component, 'builderType', component.componentType)
	set(component, 'componentType', 'Builder')
}

export const fixBuilderLegacyComponent = (component: Component, refType: string) => {
	set(component, 'componentType', refType)
}

export const fixStructureWithBuilderComponents = (structure: Structure, builderComponents: Set<string>) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const isBuilderComponent = comp && builderComponents.has(comp.componentType)
		if (isBuilderComponent) {
			fixBuilderComponent(comp)
		}
	}
	return structure
}

export const fixStructureWithBuilderLegacyComponents = (
	structure: Structure,
	builderLegacyComponentsMap: BuilderLegacyComponentsMap
) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const refType = builderLegacyComponentsMap[comp.componentType]
		if (refType !== undefined) {
			fixBuilderLegacyComponent(comp, refType)
		}
	}

	return structure
}

export const BuilderComponentType = 'BuilderComponent'

export const getBuilderComponentUrl = (app: AppSpecData, widgetId: string): string => {
	const widget = app?.widgets ? app?.widgets[widgetId] : {}
	// @ts-ignore
	return widget?.componentFields?.appStudioFields?.componentModel?.componentUrl
}

export const isBuilderComponent = (app: AppSpecData, widgetId: string): boolean =>
	!!getBuilderComponentUrl(app, widgetId)

const removeHash = (value: string) => value && value.replace('#', '')

const getCompAppData = (comp: Component, documentData: DataMap) => {
	const dataQuery = removeHash(comp.dataQuery || '')
	return documentData[dataQuery] as WidgetRefDataItem | InternalRefDataItem
}

const structureFixer = (
	structure: Structure,
	documentData: any,
	csm: ViewerClientSpecMap,
	builderWidgetsIds: Record<string, string>
) => {
	for (const compId in structure) {
		const comp = structure[compId]
		if (comp.componentType.endsWith('RefComponent')) {
			const compAppData = getCompAppData(comp, documentData)

			if (compAppData.type === 'WidgetRef') {
				const { appDefinitionId, widgetId } = compAppData
				const app = csm[appDefinitionId]
				if (!!builderWidgetsIds[widgetId] || isBuilderComponent(app, widgetId)) {
					structure[compId].componentType = BuilderComponentType
				}
			} else {
				const { pageId } = compAppData
				if (pageId && builderWidgetsIds[pageId]) {
					structure[compId].componentType = BuilderComponentType
				}
			}
		}
	}
	return structure
}

export const fixBuilderComponentType = ({
	masterPageStructure,
	structure,
	data: { document_data: documentData },
	clientSpecMap,
	builderWidgetsIds,
}: {
	masterPageStructure: Structure
	structure: Structure
	data: Data
	clientSpecMap: ViewerClientSpecMap
	builderWidgetsIds: Record<string, string>
}) => {
	const csmByAppDefId = mapKeys(clientSpecMap, (app) => app.appDefinitionId)

	return {
		masterPageStructure: structureFixer(masterPageStructure, documentData, csmByAppDefId, builderWidgetsIds),
		structure: structureFixer(structure, documentData, csmByAppDefId, builderWidgetsIds),
	}
}

export const getBuilderComponentsAppDefIds = (clientSpecMap: ViewerClientSpecMap): Array<string> =>
	Object.values(clientSpecMap)
		.filter((app: AppSpecData) => app.components?.some((comp) => comp.type === 'EDITOR_REACT_COMPONENT'))
		.map((app: AppSpecData) => app.appDefinitionId)

export const getRefsForAppDefId = async (_appDefId: string): Promise<RefSchemas> => {
	// This should be the response from the server
	return Promise.resolve({
		document_data: {
			SOME_BUILDER_COMPONENT: [
				{
					jsonPointer: '/',
					isList: false,
					referencedMap: 'document_data',
					shouldCollect: true,
					isRefOwner: true,
					shouldValidate: true,
					refTypes: ['SVGItem'],
					path: [''],
					isRelationalSplit: false,
				},
			],
		},
	})
}

export const mergeRefs = (appDefIdToRefs: Record<string, RefSchemas>): RefSchemas =>
	Object.values(appDefIdToRefs).reduce((acc, refs) => {
		return Object.entries(refs).reduce((accMap, [mapName, mapRefs]) => {
			const existingMapRefs = accMap[mapName] || {}
			const mergedDataTypes = Object.entries(mapRefs).reduce((accDataTypes, [dataType, refInfoArray]) => {
				const existingRefInfoArray = existingMapRefs[dataType] || []
				return {
					...accDataTypes,
					[dataType]: [...existingRefInfoArray, ...refInfoArray],
				}
			}, existingMapRefs)

			return {
				...accMap,
				[mapName]: mergedDataTypes,
			}
		}, acc)
	}, {} as RefSchemas)

export const getBuilderComponentRefPromises = (
	builderComponentsAppDefIds: Array<string>
): Array<{ appDefId: string; refsPromise: Promise<RefSchemas> }> => {
	return builderComponentsAppDefIds.map((appDefId) => {
		return { appDefId, refsPromise: getRefsForAppDefId(appDefId) }
	})
}

export const formatToAppDefIdToRefs = (
	results: Array<{ appDefId: string; refs: RefSchemas }>
): Record<string, RefSchemas> => {
	return results.reduce((acc, { appDefId, refs }) => {
		acc[appDefId] = refs
		return acc
	}, {} as Record<string, RefSchemas>)
}

export const getBuilderComponentRefSchemas = async (
	clientSpecMap: ViewerClientSpecMap
): Promise<Record<string, RefSchemas>> => {
	const builderComponentsAppDefIds = getBuilderComponentsAppDefIds(clientSpecMap)
	const refPromises = getBuilderComponentRefPromises(builderComponentsAppDefIds)

	const results = await Promise.all(
		refPromises.map(async ({ appDefId, refsPromise }) => ({ appDefId, refs: await refsPromise }))
	)

	return formatToAppDefIdToRefs(results)
}
